"use client"
import Banner from "../shared/Banner"
import BannerPlaceholder from "../shared/BannerPlaceholder"
import ContactUsInfo from "./ContactUsInfo"
import { TBannerResponse } from "@/app/types/Shared/BannerAndShortVideo"
import useMediaGetter from "@/app/hooks/useMediaGetter"
import { useSearchParams } from "next/navigation"
import { useContext, useEffect, useRef } from "react"
import { scrollToSection } from "@/app/utils/scrollToSection"
import { screenSizeContext } from "@/app/store/context/screenSizesContext"
import { loadingContext } from "@/app/store/context/isLoadingContext"
import dynamic from "next/dynamic"

const GetInTouch = dynamic(() => import("../shared/form/GetInTouch"), {
	loading: () => (
		<div id="get_in_touch" className="min-h-[32rem] bg-white" aria-hidden />
	),
})

const ContactUsComponent = ({
	data,
	banner,
	destinationData,
	initialBannerImageSrc,
}: {
	data: any
	banner: TBannerResponse
	destinationData?: any
	initialBannerImageSrc?: string
}) => {
	const {isPageFetched, setIsPageFetched} = useContext(loadingContext)
	const {isIpad} = useContext(screenSizeContext)
	const { imgSrc } = useMediaGetter(banner?.media)
	const bannerImageSrc = imgSrc ?? initialBannerImageSrc

	const params = useSearchParams();
	const booking = params.get("book");

	useEffect(() => {
		if (booking) scrollToSection("get_in_touch", -810);
	}, [booking]);

	useEffect(()=> {
		if(bannerImageSrc) {
			setTimeout(()=> {
				setIsPageFetched((prev: string)=> {
					if(prev === "loading") return "fetched";
					return prev;
				});
			})
		}
	}, [bannerImageSrc, setIsPageFetched])

	const tl = useRef<GSAPTimeline | null>(null);
	useEffect(() => {
		if (isPageFetched === "fetched" && bannerImageSrc) {
			let cancelled = false;

			const timer = setTimeout(async () => {
				const { default: gsap } = await import("gsap");
				if (cancelled) return;

				const html = document.documentElement;

				tl.current?.kill();
				tl.current = gsap.timeline();
				tl.current
				.timeScale(html.classList.contains("loaded") ? 1.25 : 1)
				.set(".banner .hero-title-word", { yPercent: 50, alpha: 0 })
				.to(".banner .hero-image img", {
					delay: html.classList.contains("loaded") ? 0 : 2,
					duration: 1,
					ease: "power2.inOut",
					y: "0%"
				})
				.to(".banner .hero-image-wrapper", {
					duration: 1,
					ease: "power2.inOut",
					scale: 1,
				}, "-=0.25")
				.to(".navbar_section", {
					duration: 1,
					ease: "power2.inOut",
					y: "0%"
				}, "-=0.4")
				.to(".banner .hero-title-word", {
					duration: 1,
					stagger: 0.05,
					ease: "expo.inOut",
					yPercent: 0,
					alpha: 1
				}, "<");
			}, 50);

			return () => {
				cancelled = true;
				clearTimeout(timer);
				tl.current?.kill();
			};
		}
	}, [isPageFetched, bannerImageSrc]);

	return (
		<div className={`page_wrapper ${isIpad && "contact-page"}`}>
			{bannerImageSrc ? (
				<Banner
					priority
					optimizedImage
					imageSrc={`${bannerImageSrc}`}
					minHeight="60vh"
					mobileHeight="60vh"
					title={banner?.title?.en}
					noDescriptionWithSmallTitle
					customClass="banner_only_header"
				/>
			) : (
				<BannerPlaceholder minHeight="60vh" mobileHeight="60vh" />
			)}
			<div className="bg-white">
				<ContactUsInfo data={data?.contact_info} />
				<GetInTouch IsWidth="lg:w-[45rem]" isContactUs destinationData={destinationData} />
			</div>
		</div>
	)
}

export default ContactUsComponent
